/**
 * The legs the user tracked on a box score's game, web and mobile alike: one page of the parlays
 * with a leg on it (the parlays endpoint's game filter), however early tracked, every result.
 */

import { useEffect, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { createParlaysApi, MAX_PARLAYS_PAGE_SIZE, type TrackedParlay } from '../api/parlaysApi';
import { type BoxScoreGame, gameDayOf, legsOnGame } from '../gameLegs';
import type { LeagueKey } from '../league';
import type { GameTrackedLeg } from './useBoxScore';

export interface UseGameTrackedLegsOptions {
  httpClient: HttpClient;
  gameId: string;
  leagueKey: LeagueKey;
  homeTeam?: string;
  awayTeam?: string;
  /** The game's tip, when known; its day dates the team match. */
  gameStartsAt?: string;
}

/** The game's legs across the parlays, one per player, stat, line and side. */
function trackedLegsOnGame(parlays: TrackedParlay[], game: BoxScoreGame): GameTrackedLeg[] {
  const seen = new Set<string>();
  const gameLegs: GameTrackedLeg[] = [];
  const allLegs = parlays.flatMap((parlay) => parlay.legs);
  for (const leg of legsOnGame(allLegs, game)) {
    const key = `${leg.player}|${leg.statType}|${leg.line}|${leg.overUnder}`;
    if (seen.has(key)) continue;
    seen.add(key);
    gameLegs.push({
      player: leg.player,
      team: leg.team || null,
      statType: leg.statType,
      line: leg.line,
      overUnder: leg.overUnder || null,
      prop: leg.prop,
      hit: leg.hit ?? null,
      actualValue: leg.actualValue,
    });
  }
  return gameLegs;
}

export function useGameTrackedLegs({
  httpClient,
  gameId,
  leagueKey,
  homeTeam,
  awayTeam,
  gameStartsAt,
}: UseGameTrackedLegsOptions): GameTrackedLeg[] {
  const [legs, setLegs] = useState<GameTrackedLeg[]>([]);

  useEffect(() => {
    // Drop the last game's legs.
    setLegs((current) => (current.length === 0 ? current : []));
    // The no-game placeholder useBoxScoreData takes too.
    if (!gameId || gameId === '__none__') return;

    const game: BoxScoreGame = {
      gameId,
      leagueKey,
      homeTeam,
      awayTeam,
      gameDay: gameDayOf(gameStartsAt),
    };
    let isCurrentGame = true;
    createParlaysApi(httpClient)
      .getParlays({ game, limit: MAX_PARLAYS_PAGE_SIZE })
      .then(({ parlays }) => {
        if (isCurrentGame) setLegs(trackedLegsOnGame(parlays, game));
      })
      .catch((err) => {
        console.error('Failed to load tracked legs for the box score:', err);
      });
    return () => {
      isCurrentGame = false;
    };
  }, [httpClient, gameId, leagueKey, homeTeam, awayTeam, gameStartsAt]);

  return legs;
}
